import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { PasswordCriteriaInfo } from "../components/ui-helpers";
import { PASSWORD_POLICY_MESSAGE, isStrongPassword } from "../utils/password";

export default function ForcePasswordChange() {
  const { user, setUser, refreshSession } = useAuth();
  const nav = useNavigate();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [msg, setMsg] = useState("");

  const [step, setStep] = React.useState(1);

  const [pin1, setPin1] = React.useState("");
  const [pin2, setPin2] = React.useState("");
  const role = user?.currentRole || "User";
  const needsPin = role !== "Viewer" && role !== "Super Admin";


  React.useEffect(() => {
    if (!user?.mustChange) {
      nav("/", { replace: true });
    }
  }, [user, nav]);

  if (!user?.mustChange) return null;

  const changePassword = async () => {
    if (!p1 || !p2) {
      setMsg("Enter and confirm your new password.");
      return;
    }
    if (!isStrongPassword(p1)) {
      setMsg(PASSWORD_POLICY_MESSAGE);
      return;
    }
    if (p1 !== p2) {
      setMsg("New password and confirm password do not match.");
      return;
    }
    try {
      const form = new FormData();
      form.append("uid", user.uid);
      form.append("role", role);
      form.append("new_password", p1);
      const res = await api.post("/auth/change_password", form);
      const data = res.data || {};
      if (data.ok) {
        if (needsPin) {
          setStep(2);
          setMsg("");
        } else {
          await refreshSession?.({ silent: true }).catch(() => null);
          const u = { ...user, mustChange: false };
          setUser(u);
          nav("/", { replace: true });
        }
      } else {
        setMsg("Failed to update password.");
      }
    } catch {
      setMsg("Failed to update password.");
    }
  };

  const savePin = async () => {
    if (!/^\d{6}$/.test(pin1) || !/^\d{6}$/.test(pin2)) {
      setMsg("PIN must be a 6-digit number.");
      return;
    }
    if (pin1 !== pin2) {
      setMsg("PINs do not match.");
      return;
    }
    try {
      const form = new FormData();
      form.append("uid", user.uid);
      form.append("role", role);
      form.append("pin", pin1);
      form.append("confirm_pin", pin2);
      const r = await api.post("/auth/set_pin", form);
      if (r.data?.ok || r.status === 200) {
        await refreshSession?.({ silent: true }).catch(() => null);
        const u = { ...user, mustChange: false };
        setUser(u);
        nav("/", { replace: true });
      } else {
        setMsg("Failed to save PIN.");
      }
    } catch {
      setMsg("Failed to save PIN.");
    }
  };
return (
  <div className="section" style={{ maxWidth: 560, margin: "24px auto" }}>
    <h2>{step === 1 ? "Change your password" : "Set 6-digit PIN"}</h2>
    {step === 1 && (
      <>
        <p>For security, you must set a new password before continuing.</p>
        <div className="helper-text" style={{ marginBottom: 8 }}>{PASSWORD_POLICY_MESSAGE}</div>
        <div className="vstack" style={{ marginTop: 8 }}>
          <label>
            <span className="field-label-inline">
              <span>New password</span>
              <PasswordCriteriaInfo />
            </span>
            <input type="password" placeholder="New password" value={p1} onChange={(e) => setP1(e.target.value)} />
          </label>
          <label>
            <span>Confirm password</span>
            <input type="password" placeholder="Confirm" value={p2} onChange={(e) => setP2(e.target.value)} />
          </label>
          {p1 && p2 && p1 !== p2 ? <div className="banner error">New password and confirm password do not match.</div> : null}
          <div className="hstack" style={{ alignItems: "center", gap: 10 }}>
            <button className="primary" onClick={changePassword}>
              Update password
            </button>
            <span>{msg}</span>
          </div>
        </div>
      </>
    )}

    {step === 2 && (
      <>
        <p>This PIN will be required for critical actions (digital signature).</p>
        <div className="vstack" style={{ marginTop: 8 }}>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="new-password"
            placeholder="Enter 6-digit PIN"
            value={pin1}
            onChange={(e) => setPin1(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
          />
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="new-password"
            placeholder="Confirm PIN"
            value={pin2}
            onChange={(e) => setPin2(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
          />
          {pin1 && !/^\d{6}$/.test(pin1) ? <div className="banner error">PIN must be a 6-digit number.</div> : null}
          {pin1 && pin2 && pin1 !== pin2 ? <div className="banner error">PINs do not match.</div> : null}
          <div className="hstack" style={{ alignItems: "center", gap: 10 }}>
            <button className="primary" disabled={pin1.length !== 6 || pin2.length !== 6 || pin1 !== pin2} onClick={savePin}>
              Save PIN
            </button>
            <span>{msg}</span>
          </div>
        </div>
      </>
    )}
  </div>
);
}
